import React from "react";
import "./NFTScale.css";

const MAX_STATS = {
    Damage: 250,
    Speed: 295,
    Armor: 60,
    "Item Find": 2.52,
    "Cargo Capacity": 250,
};

const MIN_STATS = {
    Damage: 130,
    Speed: 150,
    Armor: 25,
    "Item Find": 0,
    "Cargo Capacity": 50,
};

const statLabels = ["Damage", "Speed", "Item Find", "Cargo Capacity", "Armor"];
const statColors = {
    Damage: "#e74c3c",        // Red
    Speed: "#27ae60",         // Green
    "Item Find": "#ffd700",   // Gold
    "Cargo Capacity": "#9b59b6", // Purple
    Armor: "#2980b9",         // Blue
};

// Helpers for color manipulation
function hexToRgb(hex) {
    // Remove #
    hex = hex.replace(/^#/, "");
    if (hex.length === 3) {
        hex = hex.split("").map((x) => x + x).join("");
    }
    const num = parseInt(hex, 16);
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}
function rgbToHex(r, g, b) {
    return (
        "#" +
        [r, g, b]
            .map((x) => {
                const hex = x.toString(16);
                return hex.length === 1 ? "0" + hex : hex;
            })
            .join("")
    );
}
function darken(hex, amt = 0.6) {
    let [r, g, b] = hexToRgb(hex);
    r = Math.floor(r * (1 - amt));
    g = Math.floor(g * (1 - amt));
    b = Math.floor(b * (1 - amt));
    return rgbToHex(r, g, b);
}
function lighten(hex, amt = 0.5) {
    let [r, g, b] = hexToRgb(hex);
    r = Math.floor(r + (255 - r) * amt);
    g = Math.floor(g + (255 - g) * amt);
    b = Math.floor(b + (255 - b) * amt);
    return rgbToHex(r, g, b);
}

function lerpColor(hex1, hex2, t) {
    // Linear interpolate between two colors
    const rgb1 = hexToRgb(hex1);
    const rgb2 = hexToRgb(hex2);
    return rgbToHex(
        Math.round(rgb1[0] + (rgb2[0] - rgb1[0]) * t),
        Math.round(rgb1[1] + (rgb2[1] - rgb1[1]) * t),
        Math.round(rgb1[2] + (rgb2[2] - rgb1[2]) * t)
    );
}

const NFTscale = ({ nft }) => {
    const defaultStats = {
        Damage: MIN_STATS.Damage,
        Speed: MIN_STATS.Speed,
        Armor: MIN_STATS.Armor,
        "Item Find": MIN_STATS["Item Find"],
        "Cargo Capacity": MIN_STATS["Cargo Capacity"],
    };

    let stats = { ...defaultStats };

    if (nft && nft.trait_stats) {
        const ts = nft.trait_stats;

        // Damage: Laser + half missile
        const laserDamage = ts.laser?.damage || 0;
        const missileDamage = ts.missile?.player_damage_missile || 0;
        const missileDisplayDamage = missileDamage / 2; // Halve for display
        stats.Damage = laserDamage + missileDisplayDamage;

        // Speed: Sum engine and afterburner movements
        const engineSpeed = (ts.engine?.movement_forward || 0) + (ts.engine?.movement_back || 0);
        const afterburnerSpeed = (ts.afterburner?.movement_up || 0) + (ts.afterburner?.movement_down || 0);
        stats.Speed = engineSpeed + afterburnerSpeed;

        // Armor: Canopy
        stats.Armor = ts.canopy?.armor || 0;

        // Item Find: Sum all item_find values in every part
        stats["Item Find"] = Object.values(ts)
            .map(part => (part && typeof part.item_find === 'number' ? part.item_find : 0))
            .reduce((a, b) => a + b, 0);

        // Cargo Capacity: star fighter part
        stats["Cargo Capacity"] = ts["star fighter"]?.cargo_capacity || 0;
    }

    const scaleData = statLabels.map(label => ({
        label,
        value: Math.max(MIN_STATS[label], Math.min(stats[label], MAX_STATS[label])),
    }));

    const numPoints = scaleData.length;
    const size = 270;
    const center = size / 2;
    const radius = (size / 2) * 0.7;

    // Get point for value/angle
    const getPoint = (value, i) => {
        const min = MIN_STATS[scaleData[i].label];
        const max = MAX_STATS[scaleData[i].label];
        let normalized = (value - min) / (max - min);
        normalized = Math.max(0, Math.min(normalized, 1));
        const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
        const r = normalized * radius;
        const x = center + r * Math.cos(angle);
        const y = center + r * Math.sin(angle);
        return [x, y];
    };

    // All apex points (actual stat values)
    const points = scaleData.map((d, i) => getPoint(d.value, i));
    // The outer edge of pentagon for max value
    const outerPoints = scaleData.map((d, i) => getPoint(MAX_STATS[d.label], i));
    // The center point
    const centerPoint = [center, center];

    // Function to get midpoint between two points
    const getMidpoint = (p1, p2) => [
        (p1[0] + p2[0]) / 2,
        (p1[1] + p2[1]) / 2
    ];

    return (
        <div className="nft-scale">
            <div className="nft-scale-chart">
                <svg viewBox={`0 0 ${size} ${size}`} className="nft-scale-svg">
                    {/* Max outline polygon */}
                    <polygon
                        points={outerPoints.map(pt => pt.join(',')).join(' ')}
                        fill="none"
                        stroke="#d8ecff"
                        strokeWidth="2"
                    />
                    {/* --- COLOR SPIKES --- */}
                    {points.map((apex, i) => {
                        const next = points[(i + 1) % numPoints];
                        const blendGradId = `edge-blend-${i}`;
                        const colorA = lighten(statColors[scaleData[i].label], 0.4);
                        const colorB = lighten(statColors[scaleData[(i + 1) % numPoints].label], 0.4);

                        return (
                            <React.Fragment key={`blend-edge-${i}`}>
                                <defs>
                                    <linearGradient
                                        id={blendGradId}
                                        x1={apex[0]}
                                        y1={apex[1]}
                                        x2={next[0]}
                                        y2={next[1]}
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop offset="0%" stopColor={colorA} />
                                        <stop offset="100%" stopColor={colorB} />
                                    </linearGradient>
                                </defs>
                                <polygon
                                    points={[
                                        apex,
                                        next,
                                        centerPoint
                                    ].map(p => p.join(',')).join(' ')}
                                    fill={`url(#${blendGradId})`}
                                    opacity="0.75" // Adjust to taste
                                />
                            </React.Fragment>
                        );
                    })}
                    {/* Overlay the crisp polygon */}
                    <polygon
                        points={points.map(pt => pt.join(',')).join(' ')}
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2"
                    />
                    {/* Points (circles) */}
                    {points.map((pt, i) => (
                        <circle key={i} cx={pt[0]} cy={pt[1]} r="4" fill={statColors[scaleData[i].label]} />
                    ))}
                    {/* Labels */}
                    {scaleData.map((d, i) => {
                        const angle = (Math.PI * 2 * i) / numPoints - Math.PI / 2;
                        const labelRadius = radius + 22;
                        const x = center + labelRadius * Math.cos(angle);
                        const y = center + labelRadius * Math.sin(angle);
                        return (
                            <text
                                key={i}
                                x={x}
                                y={y}
                                textAnchor="middle"
                                fill={statColors[d.label]}
                                fontSize="14"
                                style={{ textShadow: "0 0 8px #111" }}
                            >
                                {d.label}
                            </text>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

export default NFTscale;
